import { X } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  AccentButtonOutline,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import {
  CardControlsApiFlags,
  DebitCardWithTruck,
  DefaultSpendCategoriesToBan,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { MccCategory } from '../../../../../../../types/apiTypes';
import { useQueryClient } from '@tanstack/react-query';
import { Select } from 'antd';

export const EditSpendCategoriesModal = ({
  isOpen,
  onClose,
  loadingCards,
  selectedCard,
}: {
  isOpen: boolean;
  onClose: () => void;
  loadingCards: boolean;
  selectedCard?: DebitCardWithTruck;
}) => {
  const { handleSubmit, setValue, watch } = useForm<any>({
    defaultValues: {
      blockedCategories: selectedCard?.banned_spend_categories || [],
    },
  });

  // Set form values when selectedCard changes
  useEffect(() => {
    if (selectedCard) {
      setValue(
        'blockedCategories',
        selectedCard?.banned_spend_categories || []
      );
    }
  }, [selectedCard, setValue]);

  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const blockedCategories = watch('blockedCategories') || [];

  const resetToDefault = () => {
    setValue('blockedCategories', DefaultSpendCategoriesToBan);
  };

  const selectAllCategories = () => {
    setValue('blockedCategories', Object.values(MccCategory));
  };

  // Reflect the new categories on-screen immediately. The update is applied
  // by an async workflow (up to ~2 min), so a plain refetch would still return
  // the old values — patch the cache optimistically instead. A later natural
  // refetch reconciles with the server.
  const patchCategoriesInCache = (categories: MccCategory[]) => {
    if (!selectedCard?.debit_card_id) return;
    queryClient.setQueryData<DebitCardWithTruck[]>(
      ['debitCards', userId],
      (old) =>
        old?.map((card) =>
          card.debit_card_id === selectedCard.debit_card_id
            ? { ...card, banned_spend_categories: categories }
            : card
        ) ?? old
    );
  };

  const onSubmit = async (data: any) => {
    if (!selectedCard?.debit_card_id) return;

    setIsSubmitting(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/${selectedCard.debit_card_id}/controls`,
        'post',
        {
          api_flag: CardControlsApiFlags.SpendCategories,
          // Opt into the async (202/409) contract so the request returns
          // immediately instead of blocking until the workflow completes.
          is_web_client: true,
          body: {
            banned_spend_category_mcc_ranges: data.blockedCategories,
          },
        },
        'Error occurred while updating spend categories.'
      );

      if (!response) {
        toast.error('Failed to save. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // 200: no change — submission matched current state
      if (response.status === 200) {
        toast.success('No changes to save.');
        setIsSubmitting(false);
        onClose();
        return;
      }

      // 202: workflow started; 409: workflow already running. Either way the
      // save is accepted — show it optimistically and let the user know it
      // takes a bit to fully apply.
      if (response.status === 202 || response.status === 409) {
        patchCategoriesInCache(data.blockedCategories);
        toast.success(
          'Spend categories saved — changes can take up to two minutes to take effect.'
        );
        setIsSubmitting(false);
        onClose();
        return;
      }

      // Unexpected status
      toast.error('Failed to save. Please try again.');
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error updating spend categories:', error);
      toast.error('Failed to save. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50"
            onClick={isSubmitting ? undefined : onClose}
          >
            <div
              className="h-auto max-h-[90%] w-full max-w-[95%] overflow-x-hidden rounded-[4px] bg-white md:max-w-[520px]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Edit Spend Categories
                </h2>
                <X
                  className={
                    isSubmitting
                      ? 'pointer-events-none opacity-40'
                      : 'hover:cursor-pointer hover:opacity-80'
                  }
                  onClick={isSubmitting ? undefined : onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {!loadingCards ? (
                <form
                  className="flex h-full w-full flex-col gap-6 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6 py-10"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div>
                    <p className="text-[16px] text-graysecondary">
                      Block specific merchant categories for this card. All
                      other categories will remain allowed.
                    </p>
                  </div>

                  <div className="flex w-full flex-col sm:space-y-4 md:flex-row md:space-x-4 md:space-y-0">
                    <AccentButtonOutline
                      addClass="rounded-full w-[150px] h-[32px]"
                      onClick={resetToDefault}
                      disabled={isSubmitting}
                    >
                      Reset to Default
                    </AccentButtonOutline>
                    <AccentButtonOutline
                      addClass="rounded-full w-[200px] h-[32px]"
                      onClick={selectAllCategories}
                      disabled={isSubmitting}
                    >
                      Select All Categories
                    </AccentButtonOutline>
                  </div>

                  <div className="flex flex-col gap-4">
                    <label className="text-[14px] font-medium text-primary">
                      Blocked Categories
                    </label>
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Select categories to block"
                      value={blockedCategories}
                      onChange={(value) => setValue('blockedCategories', value)}
                      options={Object.values(MccCategory).map((category) => ({
                        value: category,
                        label: category,
                      }))}
                      showSearch
                      filterOption={(input, option) =>
                        (
                          option?.label?.toString().toLowerCase() || ''
                        ).includes(input.toLowerCase())
                      }
                      maxTagCount={15}
                      listHeight={300}
                      disabled={isSubmitting}
                    />
                  </div>
                </form>
              ) : (
                <div className="flex h-full w-full items-center justify-center align-middle">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingCards}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                </div>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-4 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px]"
                  disabled={isSubmitting}
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600"
                >
                  {!isSubmitting ? (
                    'Save'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={true}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default EditSpendCategoriesModal;
